import PropTypes from "prop-types";
import { useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import Notice from "Payments/Shared/Notice";
import { shouldShowNeedsUpdating } from "Payments/Shared/shouldShowNeedsUpdating";

import FnrHoverButton from "UI/FnrHoverButton";
import FnrIcon from "UI/FnrIcon";

import { formatAmountInCentsForDisplay } from "Utilities/Number/formatAmountInCentsForDisplay";

import { formatDateString } from "../../Shared/formatDateString";

import CreatedBy from "./CreatedBy";
import SkippedPaymentsButton from "./SkippedPaymentsButton";
import StatusNotificationIcon from "./StatusNotificationIcon";

RecurringGiftItem.propTypes = {
  // Gift Attributes
  ID: PropTypes.number,
  fundName: PropTypes.string,
  amount: PropTypes.number,
  interval: PropTypes.string,
  userCanEdit: PropTypes.bool,
  paymentDates: PropTypes.array,
  paymentType: PropTypes.string,
  status: PropTypes.string,
  failedPaymentsCount: PropTypes.number,
  lastConsecutiveFailedPayments: PropTypes.array,
  futureSkippedPayments: PropTypes.array,
  designations: PropTypes.array,
  // State/UI Management
  isLastItem: PropTypes.bool,
  hideAllEditButtons: PropTypes.bool,
  createdBy: PropTypes.number,
  createdByPhotoURL: PropTypes.string,
  createdByName: PropTypes.string,
  campaignName: PropTypes.string,
  applyToExistingPledge: PropTypes.bool,
  isPaused: PropTypes.bool,
  paymentMethod: PropTypes.object,
};

export default function RecurringGiftItem({
  ID,
  fundName,
  amount,
  interval,
  userCanEdit,
  paymentDates,
  paymentType,
  isLastItem,
  hideAllMoreButtons,
  campaignName,
  status,
  failedPaymentsCount,
  lastConsecutiveFailedPayments,
  futureSkippedPayments,
  hideAllEditButtons,
  applyToExistingPledge,
  isPaused,
  designations = [],
  paymentMethod,
  createdBy,
  createdByName,
  createdByPhotoURL,
}) {
  const hasAddonGifts = useMemo(
    () =>
      designations.some(({ givingAddOnGiftID }) => Boolean(givingAddOnGiftID)),
    [designations]
  );
  const editButtonRef = useRef();

  const history = useHistory();
  const location = useLocation();

  const formattedAmount = formatAmountInCentsForDisplay(amount);
  const nextPaymentDate = paymentDates[0] || null;
  const formattedDate = formatDateString(nextPaymentDate);

  const showUpdateNotice = shouldShowNeedsUpdating(paymentMethod);

  const renderNextPaymentDate = () => {
    switch (status) {
      case "active":
        return <div>&nbsp;{formattedDate}</div>;
      case "paused":
        return null;
      case "stopped":
        return <StatusLineText>stopped</StatusLineText>;
      case "expired":
        return <div>&nbsp;N/A</div>;
      default:
        return null;
    }
  };

  const setModalUrl = () => {
    history.push({
      pathname: `${location.pathname}/recurring/${ID}`,
      search: location.search,
    });
  };

  const loadUserProfile = (userID = null) => {
    userID = userID || createdBy;
    if (!userID || isNaN(userID)) return;
    window.fnReactBus?.loadUserProfile(userID);
  };

  return (
    <Gift data-cy="recurringGiftItem" hideBorder={isLastItem}>
      <Fund>
        <div>
          <FundName>
            {fundName}
            {hasAddonGifts && " +"}
          </FundName>
          {campaignName && applyToExistingPledge ? (
            <Campaign>{campaignName}</Campaign>
          ) : null}
          {showUpdateNotice && <UpdateNotice>Needs updating</UpdateNotice>}
        </div>

        <StatusNotificationIcon
          status={status}
          failedPaymentsCount={failedPaymentsCount}
          lastConsecutiveFailedPayments={lastConsecutiveFailedPayments}
          paymentType={paymentType}
          ID={ID}
          paymentDates={paymentDates}
        />

        <SkippedPaymentsButton
          futureSkippedPayments={futureSkippedPayments}
          paymentType={paymentType}
          status={status}
        />
      </Fund>

      <PaymentSection padRight={!hideAllEditButtons}>
        {userCanEdit && !hideAllEditButtons && (
          <EditButtonContainer>
            <FnrHoverButton
              ref={editButtonRef}
              onClick={setModalUrl}
              data-cy="viewRecurringBtn"
              bgColor="var(--textPrimary)"
              hoverOpacity={0.2}
              style={{ padding: "6px 8px" }}
            >
              <FnrIcon fad fa-pencil />
            </FnrHoverButton>
          </EditButtonContainer>
        )}
        <AmountSection>
          <AmountLine data-cy="recurringGiftItemAmount" $inactive={isPaused}>
            <Amount>${formattedAmount}</Amount> <Interval>{interval}</Interval>
            {status === "active" && (
              <NextPaymentLine data-cy="recurringGiftItemNextPayment">
                Next {paymentType} {formattedDate}
              </NextPaymentLine>
            )}
          </AmountLine>
        </AmountSection>
      </PaymentSection>

      <CreatedBy
        onClick={loadUserProfile}
        createdByPhotoURL={createdByPhotoURL}
        createdByName={createdByName}
        createdByUserID={createdBy}
      />
    </Gift>
  );
}

const Gift = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  border-bottom: ${({ hideBorder }) =>
    hideBorder ? `none` : `1px solid #d4d9db`};
  font-size: 20px;
  padding: 20px 0 10px 0;
  &:last-child {
    border-bottom: none;
  }
`;

const Fund = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  align-items: flex-start;
  max-width: 240px;
`;

const Campaign = styled.div`
  font-size: 14px;
  gap: 6px;
  opacity: 0.75;
`;

const FundName = styled.div`
  margin-right: 2px;
`;

const PaymentSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-grow: 1;
  margin-left: 40px;
  position: relative;
`;

const AmountSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const AmountLine = styled.div`
  display: flex;
  align-items: stretch;
  border: 3px solid #384a5d;
  border: 3px solid var(--textPrimary);
  border-radius: 8px;
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.4) 1px 1px 4px;
  opacity: ${({ $inactive }) => ($inactive ? "0.5" : "1")};
`;
const Amount = styled.div`
  font-weight: 800;
  color: var(--textInverse);
  font-size: 22px;
  background: var(--textPrimary);
  padding: 0 8px;
  padding-left: 6px;
  margin-right: -3px;
`;
const Interval = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 600;
  padding: 0 7px;
  border: 3px solid var(--textPrimary);
  display: flex;
  align-items: center;
  border-radius: 8px;
  margin-top: -3px;
  margin-bottom: -3px;
  margin-right: -3px;
`;

const NextPaymentLine = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  opacity: 0.75;
  text-transform: uppercase;
  position: absolute;
  left: 50%;
  white-space: nowrap;
  top: 100%;
  margin-top: 6px;
  font-weight: 400;
  transform: translateX(-50%);
`;

const EditButtonContainer = styled.div`
  margin-right: 6px;
  font-size: 14px;
  transition: opacity 0.2s;
  opacity: 0.6;
  ${Gift}:hover & {
    opacity: 1;
  }
`;

const StatusLineText = styled.div`
  text-transform: uppercase;
  margin-left: 3px;
  color: #f0243a;
  font-size: 14px;
  font-weight: 400;

  @media (max-width: 500px) {
    margin-left: 0;
  }
`;

const UpdateNotice = styled(Notice)`
  border-width: 1px;
  font-size: 12px;
  margin-top: 4px;
  width: fit-content;
  border-radius: 6px;
`;